import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { trackLoginSuccess, trackSignupSuccess } from "@/lib/track";

const AuthContext = createContext(null);

/* Auth state is server-side: `access_token` / `session_token` HttpOnly cookies
   are set by the backend on login/register/session/refresh and carried on
   every request via `withCredentials: true`. We no longer store a JWT in
   localStorage on the browser (2026-07-21 audit hardening — closes the
   "future-XSS steals the token" risk raised in SEC-001 defence-in-depth).

   The backend still returns `token` in the response body for tests and
   programmatic clients; we intentionally do not persist it here. */

const LEGACY_KEY = "vetsim_token";

function scrubLegacyToken() {
  try {
    if (typeof localStorage !== "undefined") localStorage.removeItem(LEGACY_KEY);
  } catch {
    /* ignore private-mode / quota */
  }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);

  useEffect(() => {
    // One-time housekeeping: any pre-hardening install may still have a token
    // sitting in localStorage. Wipe it so it can't be read by future XSS.
    scrubLegacyToken();

    // CRITICAL: If returning from the Google OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash && window.location.hash.includes("session_id=")) {
      setAuthReady(true);
      return;
    }
    api
      .get("/api/auth/me")
      .then(({ data }) => setUser(data.user))
      .catch(() => { /* unauthenticated — cookies invalid or absent */ })
      .finally(() => setAuthReady(true));
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/api/auth/login", { email, password });
    setUser(data.user);
    trackLoginSuccess();
    return data.user;
  }, []);

  const register = useCallback(async (name, email, password) => {
    const { data } = await api.post("/api/auth/register", { name, email, password });
    setUser(data.user);
    trackSignupSuccess();
    return data.user;
  }, []);

  const loginWithGoogle = useCallback(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const completeGoogleSession = useCallback(async (sessionId) => {
    const { data } = await api.post("/api/auth/session", { session_id: sessionId });
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/api/auth/logout");
    } catch {
      /* ignore */
    }
    scrubLegacyToken();
    setUser(null);
  }, []);

  const changePassword = useCallback(async (current_password, new_password) => {
    const { data } = await api.post("/api/auth/change-password", {
      current_password,
      new_password,
    });
    return data;
  }, []);

  const logoutAll = useCallback(async () => {
    await api.post("/api/auth/logout-all");
    scrubLegacyToken();
    setUser(null);
  }, []);

  const deleteAccount = useCallback(async (password) => {
    await api.delete("/api/auth/me", { data: { password } });
    scrubLegacyToken();
    setUser(null);
  }, []);

  const resendVerification = useCallback(async () => {
    const { data } = await api.post("/api/auth/resend-verification");
    return data;
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        authReady,
        login,
        register,
        loginWithGoogle,
        completeGoogleSession,
        logout,
        changePassword,
        logoutAll,
        deleteAccount,
        resendVerification,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
